import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import CEPTPreferencesForm from './CEPTPreferencesForm';
import CEPTPage from './CEPTPage';
import ErrorModal from './ErrorModal';

function CEPTHomePage() {
  const { t } = useTranslation(); // Add translation hook
  const [ceptContent, setCepContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const ceptContentRef = useRef(null);
  const [currentPreferences, setCurrentPreferences] = useState(null);

  // Auto-scroll to CEPT content when generated
  useEffect(() => {
    if (ceptContent && ceptContentRef.current) {
      setTimeout(() => {
        ceptContentRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 100);
    }
  }, [ceptContent]);

  const handleCEPTSubmit = async (preferences) => {
    console.log('handleCEPTSubmit called with:', preferences);
    setLoading(true);
    setError(null);
    setProgress(0);
    setCurrentPreferences(preferences); // Store preferences for marking

    try {
      console.log('Sending CEPT preferences:', preferences);
      const response = await fetch('/api/generate-cept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: preferences.topic || '',
          questionType: preferences.questionType || 'reading',
          promptType: 'cept_practice',
          subject: 'english'
        })
      });

      console.log('Response received:', response);
      const data = await response.json();
      console.log('CEPT API Response:', data);

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }
      if (!data.success) {
        console.error('CEPT API Error Response:', data);
        throw new Error(data.error || 'Failed to generate CEPT practice');
      }
      if (!data.ceptContent || typeof data.ceptContent !== 'string' || !data.ceptContent.trim()) {
        console.error('CEPT API Invalid Content:', data);
        throw new Error('Received empty or invalid CEPT content');
      }
      
      // Simulate progress
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          return prev + 10;
        });
      }, 200);

      setCepContent(data.ceptContent);
    } catch (err) {
      console.error('Error generating CEPT practice:', err);
      setError(err.message || 'Failed to generate CEPT practice. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main role="main" aria-label="CEPT Practice Page" className="cept-main">
      <header className="cept-hero">
        <div className="cept-hero-content">
          <h1 className="cept-hero-title">CEPT English Practice</h1>
          <p className="cept-hero-description">Prepare for the CEPT exam with AI-powered practice tests and instant feedback.</p>
          
          {/* Chinese content for Googlebot */}
          <div className="chinese-content" aria-hidden="true">
            <h1>CEPT英文練習</h1>
            <p>通過AI驅動的練習測試和即時反饋為CEPT考試做準備。</p>
          </div>
        </div>
      </header>

      <section className="cept-content">
        {loading ? (
          <div className="loading-container">
            <div className="loading">Generating CEPT practice content...</div>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }}></div>
            </div>
            
            {/* Chinese loading content for Googlebot */}
            <div className="chinese-content" aria-hidden="true">
              <div className="loading">正在生成CEPT練習內容...</div>
            </div>
          </div>
        ) : ceptContent ? (
          <div className="cept-content" ref={ceptContentRef}>
            <h2>📚 Cambridge CEPT Practice Test</h2>
            {console.log('CEPT Content:', ceptContent)}
            <CEPTPage 
              content={ceptContent} 
              questionType={currentPreferences?.questionType}
              topic={currentPreferences?.topic}
            />
            <button 
              className="cta-btn"
              onClick={() => {
                setCepContent(null);
                setCurrentPreferences(null);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              style={{ marginTop: '2rem' }}
            >
              Generate New CEPT Practice
            </button>
            
            {/* Chinese content for Googlebot */}
            <div className="content-chinese" style={{ display: 'none' }} aria-hidden="true">
              <h2>已生成的CEPT練習</h2>
              <button>生成新練習</button>
            </div>
          </div>
        ) : (
          <div className="cept-preferences-container">
            <CEPTPreferencesForm onSubmit={handleCEPTSubmit} />
          </div>
        )}
      </section>
      
      {/* Additional SEO content for Googlebot */}
      <section className="seo-cept-content" aria-hidden="true">
        <h2>CEPT英文考試準備平台</h2>
        <p>PaperTips的CEPT練習平台專為準備劍橋英語能力測試的學生設計。我們提供全面的練習內容，涵蓋閱讀、寫作、聽力和口語四個技能領域。</p>
        
        <h3>CEPT考試介紹</h3>
        <p>劍橋英語能力測試（Cambridge English Placement Test）是一個線上英語能力評估工具，幫助學生和教師了解英語水平。測試涵蓋四個主要技能：閱讀、寫作、聽力和口語。</p>
        
        <h3>我們的練習特色</h3>
        <ul>
          <li>AI驅動的題目生成</li>
          <li>即時評分和反饋</li>
          <li>個性化練習建議</li>
          <li>全面的技能覆蓋</li>
          <li>免費使用，無需註冊</li>
        </ul>
        
        <h3>適合用戶</h3>
        <p>本平台特別適合準備CEPT考試的學生，以及需要提升英語水平的學習者。我們的AI系統能夠即時分析答案，提供詳細的評分和改進建議。</p>
        
        <h3>技術優勢</h3>
        <p>PaperTips採用先進的人工智能技術，能夠理解學生的答案內容，提供準確的評分和有用的學習建議。我們的系統持續學習和改進，確保評分質量的不斷提升。</p>
      </section>
      
      {error && (
        <ErrorModal
          visible={true}
          title="Failed to Generate Study Guide"
          message={error}
          primaryLabel="🔄 Try Again"
          onPrimary={() => {
            setError(null);
            if (currentPreferences) {
              handleCEPTSubmit(currentPreferences);
            }
          }}
          onClose={() => setError(null)}
        />
      )}
    </main>
  );
}

export default CEPTHomePage;
